import { Component, ReactNode } from 'react';
import { Hub } from 'aws-amplify';
import { connect } from 'react-redux';
import { showWarningMessage, showErrorMessage } from '../../slices/notifications';
import { RouteComponentProps, withRouter } from 'react-router';
import { CognitoAction, cognitoCurrentToken, CognitoErrors, CognitoEvents, cognitoLogout, loadAmplifyConfig } from '../../services/cognito';
import {
  IUserSocialLoginRequest,
  IUserSocialSignUpPayload,
  userSocialLogin,
  userSocialSignUpPro
} from '../../slices/auth/login.thunks';
import { LoggingMode } from '../../slices/auth/authState';
import { RootState } from '../../slices/rootReducer';
import { selectors as authSelectors, setWebClientId } from '../../slices/auth';
import { setSignUpMode, SignUpMode } from '../../slices/signup/signup.slices';
import { getQueryParam } from '../../utils/queryParameters';
import { isIncrowdClient, isPureProfileClient } from '../../utils/clients';
import { AccessTypes } from '../common/domain/accountAccessType';

interface DispatchProps {
  userSocialLogin: (loginValues: IUserSocialLoginRequest) => ReturnType<ReturnType<typeof userSocialLogin>>;
  showWarningMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
  setSignUpMode: (mode: SignUpMode) => void;
  setWebClientId: typeof setWebClientId;
  userSocialSignUpPro: (params: IUserSocialSignUpPayload) => void;
}

interface IMapStateProps {
  authError: string | null;
  loggingMode: LoggingMode;
  webClientId: string | null;
}

interface IState {
  appClientId: string | null;
}

type Props = IMapStateProps & DispatchProps & RouteComponentProps;

const getWebClientIdFromParams = (): string | null => {
  const sxClientId = getQueryParam('sx_client_id'); // clientId from query param when redirected from core app
  const stateFromCognitoRedirect = getQueryParam('state'); // State from cognito redirect
  if(stateFromCognitoRedirect) {
    try {
      const jsonStringifiedStr = Buffer.from(stateFromCognitoRedirect, 'base64').toString();
      const { sx_client_id } = JSON.parse(jsonStringifiedStr);
      return sxClientId || sx_client_id;
    } catch(e) {
      console.error('Error parsing state');
    }
  }
  return sxClientId;
};

class AmplifyContainer extends Component<Props, IState> {
  constructor(props: Props) {
    super(props);
    this.socialSignIn = this.socialSignIn.bind(this);
    this.handleSignInFailure = this.handleSignInFailure.bind(this);
    if(isIncrowdClient()) {
      const webClientId = getWebClientIdFromParams();
      this.state = {
        appClientId: webClientId
      };
    }
  }

  componentDidMount() {
    if(isIncrowdClient()) {
      const { setWebClientId, webClientId } = this.props;
      const { appClientId } = this.state;
      setWebClientId( appClientId || webClientId);
      loadAmplifyConfig(appClientId);
    } else {
      loadAmplifyConfig();
    }

    Hub.listen('auth', (listenPayload) => {
      const { payload: { data, event } = {} } = listenPayload;
      const { message = '' } = data ?? {};

      switch (event) {
      case CognitoEvents.SignInFailure:
        this.handleSignInFailure(message);
        break;
      case CognitoEvents.SignInSuccess: // useful for incrowd. Please do not delete
        if(isIncrowdClient()) {
          this.socialSignInOrSignUp(CognitoAction.Login);
        }
        break;
      case CognitoEvents.CustomOAuthState:
        const { action, redirectUrl = null } = JSON.parse(data);
        this.socialSignInOrSignUp(action, redirectUrl);
        break;
      case CognitoEvents.CognitoHostedUi: // Please do not delete. control idp-initiated sso flow
        if(!isPureProfileClient()) { // This avoid user creation twing on sign up
          this.socialSignInOrSignUp(CognitoAction.Login);
        }
        break;
      }
    });
  }

  private handleSignInFailure(message: string): void {
    const { showWarningMessage, showErrorMessage, history } = this.props;

    switch(message) {
    case CognitoErrors.UserIsNotEnabled:
    case CognitoErrors.InvalidGrant:
      showWarningMessage('You no longer have an active account. Contact sales@sightx.io to sign up today.');
      break;
    case CognitoErrors.IdentityProviderMismatch:
      showErrorMessage('This email does not match with your SSO account');
      break;
    case 'Incorrect username or password.':
      showErrorMessage(message);
    }

    history.replace('/login');
  }

  private socialSignInOrSignUp(action: CognitoAction, redirectUrl?: string | null) {
    const { setSignUpMode } = this.props;

    switch(action) {
    case CognitoAction.Login:
      this.socialSignIn();
      break;
    case CognitoAction.SignUp:
      setSignUpMode(SignUpMode.Social);
      break;
    case CognitoAction.SignUpPro:
      this.socialSignUp(AccessTypes.Professional);
      break;
    case CognitoAction.SignUpLite:
      this.socialSignUp(AccessTypes.Lite);
      break;
    default:
      cognitoLogout();
    }
  }

  private async socialSignIn() {
    const token = await cognitoCurrentToken();
    if (!token) {
      return;
    }
    const action = await this.props.userSocialLogin({ token });

    if (action.type === userSocialLogin.rejected.type) {
      cognitoLogout();
    }
  }

  private async socialSignUp(accessType: AccessTypes) {
    const { userSocialSignUpPro } = this.props;
    const token = await cognitoCurrentToken();
    userSocialSignUpPro({ token, accessType });
  }

  render(): ReactNode {
    return null;
  }
}

const mapDispatchToProps = {
  showWarningMessage,
  showErrorMessage,
  userSocialLogin,
  setSignUpMode,
  setWebClientId,
  userSocialSignUpPro
};

const mapStateToProps = (state: RootState) => ({
  isLoading: authSelectors.getLoadingAuthState(state),
  authError: authSelectors.getAuthError(state),
  loggingMode: authSelectors.getLoggingMode(state),
  webClientId: authSelectors.getWebClientId(state)
});

const connectedAmplifyContainer = connect(mapStateToProps, mapDispatchToProps)(AmplifyContainer);

const amplifyContainerWithRouter = withRouter(connectedAmplifyContainer);

export { amplifyContainerWithRouter as AmplifyContainer };
